import type { LucideIcon } from 'lucide-react';
import {
  DownloadIcon,
  FileTextIcon,
  FolderOpenIcon,
  KeyRoundIcon,
  LibraryBigIcon,
  ListTodoIcon,
  LockIcon,
  SearchIcon,
  SparklesIcon,
  SquareKanbanIcon,
  WebhookIcon,
  ZapIcon,
} from 'lucide-react';

/**
 * The marks the six empty states carry (spec 122 FR-022).
 *
 * A mark, not an illustration. The constitution keeps empty states
 * typographic with line-art icons, and the brandbook's icon idiom is simple
 * line art in one colour (FD-15). A mark is that idiom at a larger size: one
 * leading glyph and one supporting glyph, arranged, in the muted foreground,
 * at the 1.5 stroke every icon in the product draws with. It gives the empty
 * region a visual centre without drawing a scene.
 *
 * Decorative throughout — the words beside it carry the meaning — so the
 * whole composition is hidden from assistive technology.
 */
function Mark({
  lead: Lead,
  support: Support,
  className,
}: {
  lead: LucideIcon;
  support: LucideIcon;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      data-slot="empty-mark"
      className="text-muted-foreground relative inline-flex size-24 items-center justify-center"
    >
      <Lead className="size-12" strokeWidth={1.5} />
      {/* The supporting glyph sits over the lead's lower right corner, on a
          disc of the surface colour so the two outlines do not cross. */}
      <span
        className={
          className ??
          'bg-background absolute right-4 bottom-4 flex size-8 items-center justify-center rounded-full'
        }
      >
        <Support className="size-6" strokeWidth={1.5} />
      </span>
    </span>
  );
}

/** An empty board: columns, and the spark that will fill them. */
export function BoardMark() {
  return <Mark lead={SquareKanbanIcon} support={SparklesIcon} />;
}

/** An empty library, or a search in it that found nothing. */
export function LibraryMark() {
  return <Mark lead={LibraryBigIcon} support={DownloadIcon} />;
}

/** No triggers configured. */
export function TriggersMark() {
  return <Mark lead={WebhookIcon} support={ZapIcon} />;
}

/** An empty folder. */
export function FilesMark() {
  return <Mark lead={FolderOpenIcon} support={FileTextIcon} />;
}

/** No tasks, or none that match. */
export function TasksMark() {
  return <Mark lead={ListTodoIcon} support={SearchIcon} />;
}

/** No secrets or variables. */
export function SecretsMark() {
  return <Mark lead={KeyRoundIcon} support={LockIcon} />;
}
